import { Pencil, Trash } from "@phosphor-icons/react"
import { useState } from "react"

import { Badge } from "~/components/reusable/Badge"
import { Button } from "~/components/reusable/Button"
import { Checkbox } from "~/components/reusable/Checkbox"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { Input } from "~/components/reusable/Input"

export type TagStatus = {
  tagId: number
  name: string
  currentCount: number
  initialCount: number
  allVariantsHaveTag: boolean
  noVariantsHaveTag: boolean
  skuCount: number
}

type TagItemProps = {
  tag: TagStatus
  totalVariantCount: number
  onToggleAdd: (tagId: number) => void
  onToggleRemove: (tagId: number) => void
  onDelete: (tagId: number) => void
  onRename: (tagId: number, newName: string) => void
}

export const TagItem = ({
  tag,
  totalVariantCount,
  onToggleAdd,
  onToggleRemove,
  onDelete,
  onRename,
}: TagItemProps) => {
  const isIndeterminate = !tag.allVariantsHaveTag && !tag.noVariantsHaveTag
  const showCount = tag.currentCount > 0
  const [renameOpen, setRenameOpen] = useState(false)
  const [newName, setNewName] = useState(tag.name)

  const handleRename = () => {
    if (newName.trim() && newName !== tag.name) {
      onRename(tag.tagId, newName.trim())
    }
    setRenameOpen(false)
  }

  return (
    <div className="group flex flex-row gap-3 items-center justify-start hover:bg-subtle rounded-md p-2">
      <Checkbox
        checked={tag.allVariantsHaveTag}
        indeterminate={isIndeterminate}
        onCheckedChange={(checked) => {
          if (checked) {
            onToggleAdd(tag.tagId)
          } else {
            onToggleRemove(tag.tagId)
          }
        }}
      />
      <Badge variant="tag">{tag.name}</Badge>
      {showCount && (
        <span className="text-sm text-muted-foreground">
          {tag.currentCount}/{totalVariantCount}
        </span>
      )}
      <div className="ml-auto flex gap-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <DialogRoot
          open={renameOpen}
          onOpenChange={(open) => {
            setRenameOpen(open)
            if (open) setNewName(tag.name)
          }}
        >
          <DialogTrigger requires="edit" asChild allowedTitle="Rename tag">
            <Button variant="transparent" size="xs">
              <Pencil size={14} />
            </Button>
          </DialogTrigger>
          <DialogContentV2>
            <DialogHeaderV2>
              <DialogTitle>Rename tag</DialogTitle>
            </DialogHeaderV2>
            <DialogBodyV2>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleRename()}
                containerClassName="w-full"
                autoFocus
              />
            </DialogBodyV2>
            <DialogFooterV2 className="justify-end gap-2">
              <Button variant="neutral" onClick={() => setRenameOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                requires="edit"
                onClick={handleRename}
                disabled={!newName.trim()}
              >
                Save
              </Button>
            </DialogFooterV2>
          </DialogContentV2>
        </DialogRoot>
        <ConfirmationDialog
          title={`Delete "${tag.name}"?`}
          description={`This will permanently delete this tag from the platform and remove it from ${tag.skuCount} ${tag.skuCount === 1 ? "SKU" : "SKUs"}. This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          requires="edit"
          allowedTitle="Delete tag"
          trigger={
            <Button variant="transparent" size="xs">
              <Trash size={14} />
            </Button>
          }
          onConfirm={() => onDelete(tag.tagId)}
        />
      </div>
    </div>
  )
}
